import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_ERROR_NOTIFICATION } from '../../error-handling/services/http-error-notification.interceptor';
import { BehaviorSubject, Subject } from 'rxjs';
import { ContextoPWA } from '../../../shared/types';
import { parseJSONSafe } from '../../../shared/utils';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '../../../shared/constants';
import { ItemEducacionalColorService } from './item-educacional-color.service';

// Re-export para manter compatibilidade com componentes existentes
export { ContextoPWA } from '../../../shared/types';

@Injectable({
  providedIn: 'root'
})
export class MobileContextService {
  private contextoSubject = new BehaviorSubject<ContextoPWA | null>(null);
  public contexto$ = this.contextoSubject.asObservable();
  
  // Observable para mudanças de contexto feitas pelo usuário (via modal)
  private contextChangedSubject = new Subject<ContextoPWA>();
  public contextChanged$ = this.contextChangedSubject.asObservable();
  
  private manualChangeActive = false; // Flag permanente para desabilitar verificações após mudança manual

  constructor(
    private http: HttpClient,
    public colorService: ItemEducacionalColorService
  ) {
    this.inicializarContexto();
    // Sempre que o contexto mudar, recarregar as cores na ordem correta (sem cache stale)
    this.contexto$.subscribe(contexto => {
      if (contexto?.componentes?.length) {
        this.colorService.setContextComponentes(contexto.componentes as Array<{ id: number; color?: string }>);
      }
    });
  }

  /**
   * Verifica se há contexto completo salvo e atualiza se mudou.
   * Não é chamado por polling — mantido apenas para uso manual on-demand.
   */
  checkAndUpdateContext(): void {
    // Após mudança manual, só o próprio usuário altera de novo
    if (this.manualChangeActive) {
      return;
    }

    // Fallback de leitura na chave antiga (__pwa_bridge_contexto_completo) por 1 release — TODO remover
    const contextoCompleto = localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
    if (!contextoCompleto) {
      return;
    }

    const contexto = parseJSONSafe<ContextoPWA | null>(contextoCompleto, null);
    if (contexto && this.hasContextChanged(contexto)) {
      this.atualizarContexto({
        servidor: contexto.servidor,
        anoLetivo: contexto.anoLetivo,
        estabelecimento: contexto.estabelecimento,
        turma: contexto.turma,
        entidade: contexto.entidade,
        componentes: contexto.componentes
      }, true);
    }
  }

  private hasContextChanged(novoContexto: ContextoPWA): boolean {
    const contextoAtual = this.contextoSubject.value;
    
    if (!contextoAtual || !contextoAtual.turma || !contextoAtual.turma.id) {
      return true;
    }

    return this.contextoIdsChanged(contextoAtual, novoContexto);
  }

  private contextoIdsChanged(atual: ContextoPWA, novo: ContextoPWA): boolean {
    return atual.servidor?.id !== novo.servidor?.id ||
           atual.turma?.id !== novo.turma?.id ||
           atual.estabelecimento?.id !== novo.estabelecimento?.id;
  }

  private inicializarContexto() {
    try {
      // Prioridade 1: objetos completos (evita chamadas HTTP)
      // Fallback de leitura na chave antiga por 1 release — TODO remover
      const bridgeContextoCompleto = localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
      if (bridgeContextoCompleto) {
        const contextoCompleto = parseJSONSafe<ContextoPWA | null>(bridgeContextoCompleto, null);
        if (contextoCompleto) {
          if (contextoCompleto.turma && contextoCompleto.turma.id) {
            // Entidade vem com "name" em inglês da bridge; internamente usamos "nome"
            let entidadeMapeada = null;
            if (contextoCompleto.entidade) {
              const entidadeRaw = contextoCompleto.entidade as any;
              entidadeMapeada = {
                id: entidadeRaw.id,
                nome: entidadeRaw.name || entidadeRaw.nome
              };
            }

            const contexto: ContextoPWA = {
              servidor: contextoCompleto.servidor || null,
              anoLetivo: contextoCompleto.anoLetivo || null,
              estabelecimento: contextoCompleto.estabelecimento || null,
              turma: contextoCompleto.turma || null,
              entidade: entidadeMapeada,
              componentes: contextoCompleto.componentes || null
            };

            this.contextoSubject.next(contexto);
            this.enriquecerContexto(contexto);
            return;
          }
        }
      }
      // Prioridade 2: app-context (apenas IDs, precisa enriquecer)
      const bridgeAppContext = localStorage.getItem(STORAGE_KEYS.BRIDGE_APP_CONTEXT) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_APP_CONTEXT);

      if (bridgeAppContext) {
        try {
          const decodedContext = JSON.parse(atob(bridgeAppContext));

          // Formato do app-context: { value: "123", insulation: false }
          if (decodedContext.turma && decodedContext.turma.value) {
            const contexto: ContextoPWA = {
              servidor: decodedContext.servidor ? { id: decodedContext.servidor } : null,
              anoLetivo: decodedContext.anoletivo ? {
                id: decodedContext.anoletivo.value,
                ano: decodedContext.anoletivo.value // usa o ID como ano por enquanto
              } : null,
              estabelecimento: decodedContext.estabelecimento ? {
                id: Number(decodedContext.estabelecimento.value)
              } : null,
              turma: decodedContext.turma ? {
                id: Number(decodedContext.turma.value)
              } : null,
              entidade: null // entidade não vem no app-context
            };

            this.contextoSubject.next(contexto);
            this.enriquecerContexto(contexto);
            return;
          }
        } catch (e) {
          // Erro ao decodificar, continuar com fallback
        }
      }

      this.contextoSubject.next(null);
    } catch (error) {
      this.contextoSubject.next(null);
    }
  }

  atualizarContexto(contextoParcial: Partial<ContextoPWA>, substituirCompletamente = false) {
    const contextoAtual = this.contextoSubject.value;
    
    let contextoAtualizado: ContextoPWA;
    
    if (substituirCompletamente) {
      // Substituição completa (ex: contexto vindo da URL): usa só os valores fornecidos
      contextoAtualizado = {
        turma: contextoParcial.turma !== undefined ? contextoParcial.turma : null,
        estabelecimento: contextoParcial.estabelecimento !== undefined ? contextoParcial.estabelecimento : null,
        anoLetivo: contextoParcial.anoLetivo !== undefined ? contextoParcial.anoLetivo : null,
        servidor: contextoParcial.servidor !== undefined ? contextoParcial.servidor : null,
        entidade: contextoParcial.entidade !== undefined ? contextoParcial.entidade : null,
        componentes: contextoParcial.componentes !== undefined ? contextoParcial.componentes : undefined
      };
    } else {
      // Merge (padrão): mantém valores atuais quando não fornecidos.
      // Se o novo estabelecimento vier só com ID, preserva nome/codigo do atual.
      if (contextoParcial.estabelecimento && contextoParcial.estabelecimento.id &&
          !contextoParcial.estabelecimento.nome && !contextoParcial.estabelecimento.codigo &&
          contextoAtual?.estabelecimento &&
          (contextoAtual.estabelecimento.nome || contextoAtual.estabelecimento.codigo)) {
        contextoParcial.estabelecimento = {
          ...contextoParcial.estabelecimento,
          nome: contextoAtual.estabelecimento.nome || contextoAtual.estabelecimento.codigo,
          codigo: contextoAtual.estabelecimento.codigo || contextoAtual.estabelecimento.nome
        };
      }
      
      contextoAtualizado = { 
        turma: contextoParcial.turma !== undefined ? contextoParcial.turma : contextoAtual?.turma,
        estabelecimento: contextoParcial.estabelecimento !== undefined ? contextoParcial.estabelecimento : contextoAtual?.estabelecimento,
        anoLetivo: contextoParcial.anoLetivo !== undefined ? contextoParcial.anoLetivo : contextoAtual?.anoLetivo,
        servidor: contextoParcial.servidor !== undefined ? contextoParcial.servidor : contextoAtual?.servidor,
        entidade: contextoParcial.entidade !== undefined ? contextoParcial.entidade : contextoAtual?.entidade,
        componentes: contextoParcial.componentes !== undefined ? contextoParcial.componentes : contextoAtual?.componentes
      };
    }
    
    this.contextoSubject.next(contextoAtualizado);
    this.enriquecerContexto(contextoAtualizado);
  }
  
  /**
   * Busca os dados completos (nome, ano, etc) do backend para o contexto
   */
  private async enriquecerContexto(contexto: ContextoPWA) {
    try {
      const contextoEnriquecido = { ...contexto };
      let atualizado = false;
      
      // Enriquecer Turma (buscar nome)
      if (contexto.turma?.id && !contexto.turma.nome && !contexto.turma.descricao) {
        try {
          // Usar endpoint context/turma como o AngularJS faz
          const turmaResp = await this.http.get<{ content?: Array<{ descricao?: string; nome?: string; serie?: string; turno?: string }> }>(
            `/api/context/turma`,
            {
              params: { filter: `id=${contexto.turma.id}`, offset: '0', limit: '1' },
              context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true)
            }
          ).toPromise();
          const turma = turmaResp?.content?.[0];
          if (turma) {
            contextoEnriquecido.turma = {
              ...contexto.turma,
              nome: turma.nome || turma.descricao || `Turma ${contexto.turma.id}`,
              descricao: turma.descricao || turma.nome || `Turma ${contexto.turma.id}`,
              serie: turma.serie,
              turno: turma.turno
            };
            atualizado = true;
          } else {
            throw new Error('Turma não encontrada via context/turma');
          }
        } catch {
          // Fallback: tentar commons/turma/{id}
          try {
            const turma = await this.http.get<{ nome?: string; descricao?: string; serie?: string; turno?: string }>(
              `/api/commons/turma/${contexto.turma.id}`,
              { context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
            ).toPromise();
            if (turma) {
              contextoEnriquecido.turma = {
                ...contexto.turma,
                nome: turma.nome || turma.descricao || `Turma ${contexto.turma.id}`,
                descricao: turma.descricao || turma.nome || `Turma ${contexto.turma.id}`,
                serie: turma.serie,
                turno: turma.turno
              };
              atualizado = true;
            }
          } catch {
            // Manter apenas com ID e nome genérico
            contextoEnriquecido.turma = {
              ...contexto.turma,
              nome: `Turma ${contexto.turma.id}`,
              descricao: `Turma ${contexto.turma.id}`
            };
            atualizado = true;
          }
        }
      }
      
      // Enriquecer Ano Letivo (buscar ano)
      if (contexto.anoLetivo?.id && !contexto.anoLetivo.ano && !contexto.anoLetivo.descricao) {
        try {
          const anoLetivo = await this.http.get<{ ano?: string | number; descricao?: string }>(`/api/commons/ano-letivo/${contexto.anoLetivo.id}`).toPromise();
          if (anoLetivo && contexto.anoLetivo) {
            contextoEnriquecido.anoLetivo = {
              ...contexto.anoLetivo,
              ano: anoLetivo.ano || (typeof anoLetivo.descricao === 'string' ? Number(anoLetivo.descricao) : undefined) || new Date().getFullYear(),
              descricao: anoLetivo.descricao || (typeof anoLetivo.ano === 'number' ? anoLetivo.ano.toString() : String(anoLetivo.ano)) || new Date().getFullYear().toString()
            };
            atualizado = true;
          }
        } catch (error) {
          // Manter apenas com ID e ano atual
          if (contexto.anoLetivo) {
            contextoEnriquecido.anoLetivo = {
              ...contexto.anoLetivo,
              ano: new Date().getFullYear(),
              descricao: new Date().getFullYear().toString()
            };
            atualizado = true;
          }
        }
      }
      
      // Enriquecer Estabelecimento (buscar nome)
      if (contexto.estabelecimento?.id) {
        if (!contexto.estabelecimento.nome && !contexto.estabelecimento.codigo) {
          // Mesma chave que o interceptor HTTP usa pra checar token
          const hasToken = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
                          localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN) ||
                          localStorage.getItem('__pwa_standalone_token') ||
                          localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
                          localStorage.getItem(STORAGE_KEYS.BRIDGE_APP_CONTEXT) ||
                          localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_APP_CONTEXT);

          if (hasToken) {
            try {
              // context/estabelecimento tem validações diferentes do commons/estabelecimento no backend
              let estabelecimento: { nome?: string; descricao?: string; codigo?: string } | null = null;

              try {
                const estabelecimentosArray = await this.http.get<Array<{ nome?: string; descricao?: string; codigo?: string; pessoa?: { nome?: string } }>>(
                  `/api/context/estabelecimento`,
                  {
                    params: { id: contexto.estabelecimento.id },
                    withCredentials: true,
                    // Serviço já trata o erro via fallback — suprimir toast global
                    context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true)
                  }
                ).toPromise();

                if (estabelecimentosArray && estabelecimentosArray.length > 0) {
                  const estabelecimentoContext = estabelecimentosArray[0];
                  estabelecimento = {
                    nome: estabelecimentoContext.pessoa?.nome || estabelecimentoContext.nome || estabelecimentoContext.descricao,
                    descricao: estabelecimentoContext.descricao || estabelecimentoContext.nome,
                    codigo: estabelecimentoContext.codigo
                  };
                }
              } catch (contextError: any) {
                // Se o commons também falhar, esse erro (mais específico) propaga pro catch externo
                const estabelecimentoCommons = await this.http.get<{ nome?: string; descricao?: string; codigo?: string }>(
                  `/api/commons/estabelecimento/${contexto.estabelecimento.id}`,
                  {
                    withCredentials: true,
                    context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true)
                  }
                ).toPromise();
                estabelecimento = estabelecimentoCommons || null;
              }

              if (estabelecimento) {
                contextoEnriquecido.estabelecimento = {
                  ...contexto.estabelecimento,
                  nome: estabelecimento.nome || estabelecimento.descricao || estabelecimento.codigo || `Escola ${contexto.estabelecimento.id}`,
                  codigo: estabelecimento.codigo || estabelecimento.descricao || estabelecimento.nome
                };
                atualizado = true;
              }
            } catch (error: any) {
              // Usar nome genérico em caso de erro (ex: 403/401)
              const status = error?.status || error?.statusCode;

              contextoEnriquecido.estabelecimento = {
                ...contexto.estabelecimento,
                nome: `Escola ${contexto.estabelecimento.id}`
              };
              atualizado = true;
            }
          } else {
            // Sem token, usar nome genérico sem tentar buscar
            contextoEnriquecido.estabelecimento = {
              ...contexto.estabelecimento,
              nome: `Escola ${contexto.estabelecimento.id}`
            };
            atualizado = true;
          }
        } else if (contexto.estabelecimento.codigo && !contexto.estabelecimento.nome) {
          contextoEnriquecido.estabelecimento = {
            ...contexto.estabelecimento,
            nome: contexto.estabelecimento.codigo
          };
          atualizado = true;
        } else if (contexto.estabelecimento.nome && !contexto.estabelecimento.codigo) {
          contextoEnriquecido.estabelecimento = {
            ...contexto.estabelecimento,
            codigo: contexto.estabelecimento.nome
          };
          atualizado = true;
        }
      }

      // Enriquecer Servidor (buscar pessoa.nome)
      if (contexto.servidor?.id && !contexto.servidor.pessoa?.nome) {
        try {
          const servidor = await this.http.get<{ id: number; pessoa?: { nome: string } }>(
            `/api/professores/servidor/filtro-gestor/${contexto.servidor.id}`,
            { context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
          ).toPromise();
          if (servidor?.pessoa?.nome) {
            contextoEnriquecido.servidor = {
              ...contexto.servidor,
              pessoa: { nome: servidor.pessoa.nome }
            };
            atualizado = true;
          }
        } catch {
          // Manter servidor apenas com ID
        }
      }

      // Enriquecer Entidade (buscar nome)
      if (contexto.entidade?.id && !contexto.entidade.nome && !contexto.entidade.name) {
        try {
          const hostname = window.location.hostname;
          const base = (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('test.betha.cloud'))
            ? 'https://plataforma-autorizacoes.test.betha.cloud'
            : 'https://plataforma-autorizacoes.betha.cloud';
          const infos = await this.http.get<Array<{ id: number; name: string }>>(
            `${base}/user-accounts/v0.1/api/entities?id=${contexto.entidade.id}`
          ).toPromise();
          if (infos?.[0]?.name) {
            contextoEnriquecido.entidade = {
              ...contexto.entidade,
              nome: infos[0].name
            };
            atualizado = true;
          }
        } catch {
          // Manter entidade apenas com ID
        }
      }

      if (atualizado) {
        this.contextoSubject.next(contextoEnriquecido);
      }
    } catch (error) {
      // Enriquecimento é best-effort — falha aqui mantém o contexto original sem nomes resolvidos
    }
  }

  getContextoAtual(): ContextoPWA | null {
    return this.contextoSubject.value;
  }

  /**
   * Persiste User-Access (licenseId) e app-context no localStorage para que o
   * interceptor HTTP os envie corretamente no modo standalone/TWA (sem AngularJS,
   * a bridge não popula essas chaves automaticamente). Chamar antes de `setContext`.
   */
  persistBridgeAccess(
    entidadeData: { licenseId?: string; access?: string; admin?: boolean } | null,
    servidorSelecionado: number | null,
    anoLetivoSelecionado: number | null,
    estabelecimentoSelecionado: number | null
  ): void {
    if (entidadeData?.licenseId) {
      localStorage.setItem(STORAGE_KEYS.BRIDGE_USER_ACCESS, entidadeData.licenseId);
      localStorage.setItem(
        STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT,
        JSON.stringify({
          licenseId: entidadeData.licenseId,
          accessId: entidadeData.access,
          admin: !!entidadeData.admin
        })
      );
      if (entidadeData.access) {
        localStorage.setItem(
          STORAGE_KEYS.BRIDGE_ACCESS_PAIR,
          JSON.stringify({
            licenseId: entidadeData.licenseId,
            accessId: entidadeData.access
          })
        );
      } else {
        localStorage.removeItem(STORAGE_KEYS.BRIDGE_ACCESS_PAIR);
      }
    }

    if (servidorSelecionado) {
      const appContextObj: Record<string, unknown> = { servidor: servidorSelecionado };
      if (anoLetivoSelecionado) {
        appContextObj['anoletivo'] = { value: anoLetivoSelecionado, insulation: false };
      }
      if (estabelecimentoSelecionado) {
        appContextObj['estabelecimento'] = { value: String(estabelecimentoSelecionado), insulation: false };
      }
      const appContextBase64 = btoa(JSON.stringify(appContextObj));
      localStorage.setItem(STORAGE_KEYS.BRIDGE_APP_CONTEXT, appContextBase64);
    }
  }

  /**
   * Define novo contexto (usado quando o usuário altera via modal) e emite
   * contextChanged$. Aguarda 100ms antes de emitir para garantir que o
   * localStorage foi atualizado e os interceptors HTTP peguem o contexto correto.
   */
  async setContext(contexto: ContextoPWA): Promise<void> {
    this.manualChangeActive = true;

    this.contextoSubject.next(contexto);
    this.saveToStorage(contexto);

    await new Promise(resolve => setTimeout(resolve, 100));

    this.contextChangedSubject.next(contexto);
  }

  /**
   * Salva contexto no localStorage (ponte AngularJS <-> PWA)
   */
  private saveToStorage(contexto: ContextoPWA): void {
    try {
      localStorage.setItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO, JSON.stringify(contexto));
    } catch (error) {
      // Persistência é best-effort — falha (ex.: quota excedida) não deve travar o fluxo
    }
  }

  limparContexto() {
    this.contextoSubject.next(null);
  }
}